import { z } from "zod";

import { logger } from "./logger";
import { ConfigSource, ResolvedConfigValue } from "./sources/config-source";

export class ConfigResolutionService<T extends Record<string, unknown>> {
  private configSources: ConfigSource<T>[] = [];

  constructor(
    readonly schema?: z.ZodSchema<T>, // Optional schema to drive the configuration values that we want. If not provided, we resolve all values from all sources
  ) {}

  public addSource(configSource: ConfigSource<T>): void {
    this.configSources.push(configSource);
    this.configSources.sort((a, b) => b.priority - a.priority);
  }

  public async initializeConfigSources(): Promise<void> {
    try {
      const initPromises = this.configSources.map((configSource) =>
        configSource.initialize(),
      );
      await Promise.all(initPromises);
    } catch (error) {
      logger.error("Failed to initialize config sources", error);
      throw error;
    }
  }

  public async resolveConfigValue<K extends keyof T>(
    key: K,
  ): Promise<ResolvedConfigValue<T[K]> | undefined> {
    try {
      for (const configSource of this.configSources) {
        const value = await configSource.getConfigValue(key);
        if (value !== undefined) {
          return {
            value,
            source: configSource.name,
          };
        }
      }
    } catch (error) {
      logger.error(
        `Failed to resolve config value for key: ${String(key)}`,
        error,
      );
      throw error;
    }
  }

  /**
   * Resolve all configuration values based on the provided schema
   */
  public async resolveConfigValues<K extends keyof T>(): Promise<
    Map<K, ResolvedConfigValue<T[K]>>
  > {
    const results = new Map<K, ResolvedConfigValue<T[K]>>();

    if (!this.schema) {
      return this.resolveAllConfig<K>(results);
    }

    const schema = this.schema as unknown as z.ZodObject<{
      [key: string]: z.ZodTypeAny;
    }>;

    // The Zod schema drives the config values that we want, use that to resolve all values
    const keys = Object.keys(schema.shape) as K[];
    const configResolutionPromises = keys.map(async (key) => {
      const resolvedConfigValue = await this.resolveConfigValue(key);
      if (resolvedConfigValue) {
        results.set(key, resolvedConfigValue);
      }
    });

    await Promise.all(configResolutionPromises);
    return results;
  }

  // Resolve all configuration values from all sources without a schema
  // This is useful when we don't have a schema at build time, or when we want to resolve all values
  private async resolveAllConfig<K extends keyof T>(
    results: Map<K, ResolvedConfigValue<T[K]>>,
  ) {
    try {
      for (const configSource of this.configSources) {
        const allConfigValuesForSource =
          await configSource.getAllConfigValues();
        Object.entries(allConfigValuesForSource).forEach(([key, value]) => {
          results.set(
            key as K,
            {
              value,
              source: configSource.name,
            } as ResolvedConfigValue<T[K]>,
          );
        });
      }

      return results;
    } catch (error) {
      logger.error("Failed to resolve all config values", error);
      throw error;
    }
  }

  /**
   * Set up change listeners on all sources
   */
  public setupChangeListeners<K extends keyof T>(
    onConfigChanged: (key: K, value: T[K], source: string) => void,
  ): void {
    this.configSources.forEach((configSource) => {
      configSource.listenForChanges?.(async (key) => {
        try {
          const resolvedConfigValue = await this.resolveConfigValue(key as K);
          if (resolvedConfigValue) {
            onConfigChanged(
              key as K,
              resolvedConfigValue.value,
              resolvedConfigValue.source,
            );
          }
        } catch (error) {
          logger.error(
            `Error in change listener for key: ${String(key)}`,
            error,
          );
        }
      });
    });
  }
}
